// Shared data + navigation helpers for the Mixpedia home rows and their
// full-page "Show more" views. Session-level caches (module scope) so
// revisiting the home/pages doesn't refetch or flash loading states.
import {
    setActiveSearch,
    setArtistQuery,
    setTrackQuery,
    setMixQuery,
    setMixFilterContext,
    setPendingMix,
} from '../../redux/slices/djpalSlice';
import { searchArtist, searchTrack, searchMixes, ensureToken } from '../../redux/thunk/djpalThunk';
import { entityId } from '../../utils/entityId';
import { track as trackEvent } from '../../utils/analytics';
import { resolveMixCover } from '../../utils/nativeMixCover';
import { coverUrl } from '../../utils/coverUrl';
import { warmImages } from '../../utils/imageWarm';

// Rendered CSS sizes of the home's art — the warms below must prime the SAME
// right-sized variant URLs the cards render (via <CoverArt>), or the warm
// fills the cache with bytes the page never asks for.
const HOME_TILE_PX = 130;   // .mh-mix-art
const HOME_AVATAR_PX = 72;  // .mh-avatar

// Mirrors what the home cards actually render: sets go through
// resolveMixCover (native tracklist substitution included), tracks/artists
// use artworkUrl.
const rowCoverUrls = (kind, items) => (items || []).map((it) => coverUrl(kind === 'sets'
    ? (resolveMixCover(it) || it.artworkUrl || null)
    : (it.artworkUrl || it.artwork || null), HOME_TILE_PX));

export const HOME_CACHE_TTL_MS = 15 * 60 * 1000;
export const HOME_ROW_LIMIT = 12;

// ---- Persisted home-rows snapshot (web + native only) ----
// Same idea as the web feed's localStorage snapshot (feedThunk): on a cold
// app launch the in-memory caches are empty, so the first Mixpedia visit
// showed skeletons for the full round-trip. Instead, the last successful
// rows are persisted and painted immediately while the real fetch
// revalidates behind them. Native uses Capacitor Preferences because iOS
// WKWebView evicts localStorage under storage pressure.
const _isWeb = typeof window !== 'undefined' && window.electron && !window.electron.db;
// The persisted snapshot (24h) now serves desktop as well: without it every
// app launch showed skeletons until the slowest row (tracks by followed
// artists played by others) answered, often 20s+.
const _snapshotOn = true;
const _capPrefs = () => (typeof window !== 'undefined'
    && window.Capacitor?.isNativePlatform?.() && window.Capacitor.Plugins?.Preferences) || null;
const HOME_SNAPSHOT_KEY = 'mg:mixpedia:home-snapshot:v1';
// A week: every visit revalidates behind the painted rows, so the age only
// matters when a row's endpoint keeps failing — then last week's row beats
// an empty one (24h dropped the slow row after a day of server trouble).
const HOME_SNAPSHOT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Hydrated once at bootstrap (App.js) so MixpediaHome can read it
// synchronously during state init — the same pre-paint requirement that
// forced the fresh-cache seeding above the useState calls.
let homeRowSnapshot = null;

const writeHomeRowSnapshot = (key) => {
    if (!_snapshotOn) return;
    try {
        const rows = {};
        for (const kind of ['sets', 'tracks', 'artistTracks']) {
            const c = homeRowCache[kind];
            // Persist only rows belonging to this key; cap to one page so
            // Show-more paging never bloats the stored payload.
            if (c.key === key && c.items.length) rows[kind] = { items: c.items.slice(0, HOME_ROW_LIMIT) };
            // A row whose fetch failed this time keeps its last good copy:
            // rewriting the snapshot without it (when another row landed)
            // erased it, so a slow endpoint left that row empty on every visit.
            else if (homeRowSnapshot?.key === key && homeRowSnapshot.rows?.[kind]?.items?.length) rows[kind] = homeRowSnapshot.rows[kind];
        }
        if (!Object.keys(rows).length) return;
        // The tracks row's shuffle seed rides along so the revalidating fetch
        // after a snapshot-seeded paint can reproduce the SAME ordering —
        // otherwise the fresh response is a different roll and visibly
        // replaces the rows the user is already reading.
        const seed = homeRowCache.tracks.key === key ? (homeRowCache.tracks.seed ?? null) : null;
        const json = JSON.stringify({ v: 1, key, savedAt: Date.now(), seed, rows });
        const prefs = _capPrefs();
        if (prefs) prefs.set({ key: HOME_SNAPSHOT_KEY, value: json });
        else localStorage.setItem(HOME_SNAPSHOT_KEY, json);
        homeRowSnapshot = JSON.parse(json);
    } catch { /* non-fatal — snapshot is an optimization */ }
};

const removeHomeRowSnapshot = () => {
    homeRowSnapshot = null;
    if (!_snapshotOn) return;
    try {
        const prefs = _capPrefs();
        if (prefs) prefs.remove({ key: HOME_SNAPSHOT_KEY });
        else localStorage.removeItem(HOME_SNAPSHOT_KEY);
    } catch { /* non-fatal */ }
};

// Async read at bootstrap (Preferences round-trips the native bridge).
export const hydrateHomeRowsSnapshot = async () => {
    if (!_snapshotOn || homeRowSnapshot) return;
    try {
        const prefs = _capPrefs();
        const raw = prefs
            ? (await prefs.get({ key: HOME_SNAPSHOT_KEY }))?.value
            : localStorage.getItem(HOME_SNAPSHOT_KEY);
        if (!raw) return;
        const snap = JSON.parse(raw);
        if (!snap || snap.v !== 1 || !snap.key || !snap.rows) return;
        if (Date.now() - (snap.savedAt || 0) > HOME_SNAPSHOT_TTL_MS) return;
        homeRowSnapshot = snap;
        // The snapshot rows will paint the instant home mounts — start their
        // covers downloading now so the pictures keep up with the text.
        for (const kind of ['sets', 'tracks', 'artistTracks']) {
            warmImages(rowCoverUrls(kind, snap.rows[kind]?.items));
        }
    } catch { /* corrupt snapshot — ignore, next successful load rewrites it */ }
};

// Key-checked accessor: the key embeds userId + the follow list, so a
// different user or a changed follow set can never paint a stale snapshot.
export const peekHomeRowSnapshot = (key) =>
    (homeRowSnapshot && homeRowSnapshot.key === key ? homeRowSnapshot.rows : null);

// kind: 'sets' | 'tracks' | 'artistTracks'. Each cache: { key, items, meta, ts }
export const homeRowCache = {
    sets: { key: null, items: [], meta: null, ts: 0 },
    tracks: { key: null, items: [], meta: null, ts: 0 },
    artistTracks: { key: null, items: [], meta: null, ts: 0 },
};

// Cache keys are scoped to the logged-in USER as well as the follow list —
// the module cache outlives a logout/login, and without the user id a new
// account could render the previous account's rows.
export const homeRowCacheKey = (userId, names) => `${userId || 'anon'}|${names.join('|')}`;

// Wipe on logout (called from the auth thunk) — module state isn't covered
// by the Redux slice clears.
export const clearHomeRowCache = () => {
    removeHomeRowSnapshot();
    homeRowCache.sets = { key: null, items: [], meta: null, ts: 0 };
    homeRowCache.tracks = { key: null, items: [], meta: null, ts: 0 };
    homeRowCache.artistTracks = { key: null, items: [], meta: null, ts: 0 };
    suggestionsCache.key = null;
    suggestionsCache.items = [];
    suggestionsCache.source = null;
    suggestionsCache.loading = false;
    suggestionsInflightKey = null;
    notifySuggestions();
};

// Follow-suggestions cache (cold-start rows) — user-scoped like the rest.
// Lives here (not in the component) so it can be warmed at startup alongside
// the sets/tracks rows and shared across mounts. Components subscribe for
// the batch-by-batch commits.
export const suggestionsCache = { key: null, items: [], source: null, loading: false };

const suggestionListeners = new Set();
export const subscribeSuggestions = (fn) => {
    suggestionListeners.add(fn);
    return () => suggestionListeners.delete(fn);
};
const notifySuggestions = () => {
    for (const fn of suggestionListeners) fn(suggestionsCache);
};

// ---- Follow-suggestions loader (moved out of MixpediaFollowSuggestions so
// the startup warmer can run it too). Primary source: the user's LOCAL
// LIBRARY — artist names counted across their tracks, resolved against
// Mixpedia (junk names simply don't resolve), enriched with artwork via
// artist autocomplete. Fallback when the library yields too little:
// globally popular artists.
const MIN_LIBRARY_MATCHES = 3;
const POPULAR_LIMIT = 100;
// Library names sent for resolution — high enough to cover a full library's
// meaningful artists while keeping one bounded request.
const MAX_RESOLVE = 200;
// Artwork enrichment runs per-name; batch to avoid hammering the server.
const ENRICH_BATCH = 8;

// Dev/testing escape hatch: simulate a brand-new user with no local library
// (forces the Popular-on-Mixpedia fallback). In DevTools:
//   localStorage.setItem('mixpedia:emptyLibrary', '1'); location.reload()
const simulateEmptyLibrary = () => {
    try { return localStorage.getItem('mixpedia:emptyLibrary') === '1'; } catch { return false; }
};

// Mirror the server's artist-name normalization (non-alphanumeric → space,
// collapse, lowercase) so "exact match" tolerates punctuation/casing only.
const normName = (s) => String(s).replace(/[^\p{L}\p{N}]+/gu, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

// Library artist fields are often filename fragments, not artists. Cheap
// rejects before we spend requests on them: mojibake, pipe separators,
// leading track-position/punctuation junk ("._", "*", "B2 ") and premiere-
// channel watermarks. Real names like "&ME" must survive.
const looksLikeJunkName = (name) => (
    name.includes('�') ||
    name.includes('|') ||
    /^[._*]/.test(name) ||
    /^[A-H][1-9]\s/i.test(name) ||
    /\b(premiere|exclusive)\b/i.test(name) ||
    name.length > 40
);

let suggestionsInflightKey = null;

export const loadFollowSuggestions = async (dispatch, getState) => {
    const userId = getState().auth.user?.id;
    const key = userId || 'anon';
    if (suggestionsCache.key === key && suggestionsCache.items.length) return;
    if (suggestionsInflightKey === key) return; // fetch already running
    suggestionsInflightKey = key;
    suggestionsCache.loading = true;
    notifySuggestions();
    try {
        const token = await ensureToken(dispatch, getState);

        // 1. Count artists across the local library.
        const rows = simulateEmptyLibrary()
            ? []
            : await (window.electron.db?.tracks?.getAll?.().catch(() => []) ?? Promise.resolve([])); // web/native shims have no local DB
        const counts = new Map();
        for (const r of (rows || [])) {
            const raw = r?.track?.artist;
            if (!raw) continue;
            for (const part of String(raw).split(',')) {
                const name = part.trim();
                if (name.length < 2 || looksLikeJunkName(name)) continue;
                counts.set(name, (counts.get(name) || 0) + 1);
            }
        }
        let topNames = [...counts.entries()]
            .sort((a, b) => b[1] - a[1])
            .slice(0, MAX_RESOLVE)
            .map(([name]) => name);

        // Web/mobile have no local DB — use the server-side DJ library (what
        // the desktop's dj-library sync pushed up) as the same ranked name
        // list. Without this branch these surfaces always fell through to
        // Popular even for users with a fully synced library.
        if (!topNames.length && !window.electron.db && !simulateEmptyLibrary()) {
            const accessToken = getState().auth.accessToken;
            const lib = (accessToken && window.electron.library?.getDJLibraryArtists)
                ? await window.electron.library.getDJLibraryArtists(accessToken, MAX_RESOLVE).catch(() => null)
                : null;
            const arr = lib?.data || lib?.results || (Array.isArray(lib) ? lib : []);
            topNames = arr
                .map(a => String(a.artist_name || a.name || '').trim())
                .filter(n => n.length >= 2 && !looksLikeJunkName(n))
                .slice(0, MAX_RESOLVE);
        }

        // 2. Keep only names Mixpedia actually knows (rank order kept).
        let resolvedNames = [];
        let resolveFailed = false;
        if (topNames.length) {
            const r = await window.electron.djpal.feedResolveArtistsV2(token, topNames).catch(() => null);
            if (!r || r.error) {
                // Transient failure — NOT "your library resolves to nothing".
                resolveFailed = true;
            } else {
                const resolved = r?.resolved || r?.data?.resolved || [];
                const resolvedSet = new Set(resolved.map(a => a.name.toLowerCase()));
                resolvedNames = topNames.filter(n => resolvedSet.has(n.toLowerCase()));
            }
        }

        const commit = (list, src) => {
            suggestionsCache.key = key;
            suggestionsCache.items = list;
            suggestionsCache.source = src;
            warmImages(list.map(a => coverUrl(a.artworkUrl, HOME_AVATAR_PX)));
            notifySuggestions();
        };

        let libraryList = null;
        if (resolvedNames.length >= MIN_LIBRARY_MATCHES) {
            // Quality gate: a card renders only when autocomplete returns an
            // exact (normalized) match — and it renders under Mixpedia's
            // canonical name. Kills the filename-junk tail ("MOTZ Premiere X"
            // won't exactly match anything) and fixes casing/spelling for
            // free. Cards append batch by batch, deduped on canonical
            // identity (two library spellings of one artist → one card).
            libraryList = [];
            const seenCanonical = new Set();
            for (let i = 0; i < resolvedNames.length; i += ENRICH_BATCH) {
                const batch = resolvedNames.slice(i, i + ENRICH_BATCH);
                const enriched = await Promise.all(batch.map(async (name) => {
                    const ac = await window.electron.djpal.autocompleteArtist(token, name).catch(() => null);
                    const hit = (ac?.results || []).find(a => normName(a.name || '') === normName(name));
                    if (!hit) return null;
                    return { name: hit.name, artworkUrl: hit.artworkUrl || null, id: hit.id ?? hit.publicId ?? null };
                }));
                const fresh = enriched.filter(Boolean).filter(a => {
                    const k = String(a.id || normName(a.name));
                    if (seenCanonical.has(k)) return false;
                    seenCanonical.add(k);
                    return true;
                });
                if (fresh.length) {
                    libraryList = [...libraryList, ...fresh];
                    commit(libraryList, 'library');
                }
            }
        }
        // resolvedNames cleared the MIN gate but every autocomplete-enrich
        // call failed (transient) — same story as resolveFailed: retry next
        // mount rather than pinning Popular over a real library.
        if (libraryList && libraryList.length === 0) return;
        if (!(libraryList && libraryList.length)) {
            // A failed library resolve must not pin generic Popular cards for
            // the whole session over a user who HAS a library — bail without
            // committing so the next mount retries the library path.
            if (resolveFailed) return;
            const r = await window.electron.djpal.getPopularArtists(token).catch(() => null);
            if (!r || r.error) return; // transient — leave cache unset so the next call retries
            const arr = r?.results || r?.data || [];
            const picked = arr.slice(0, POPULAR_LIMIT).map(a => ({
                name: a.name || a.artist_name || '',
                artworkUrl: a.artworkUrl || a.artwork || null,
                id: a.id ?? a.publicId ?? null,
            })).filter(a => a.name);
            if (picked.length) commit(picked, 'popular');
        }
    } finally {
        suggestionsCache.loading = false;
        suggestionsInflightKey = null;
        notifySuggestions();
    }
};

export const cacheIsFresh = (kind, key) =>
    homeRowCache[kind].key === key && (Date.now() - homeRowCache[kind].ts) < HOME_CACHE_TTL_MS;

// Stale-but-same-key rows: past the TTL, but still THIS user's + follow
// set's last successful result. Used as the stale-while-revalidate seed on
// desktop, where there is no persisted snapshot — without it, a home visit
// after >15 min in the app (startup prefetch expired, nothing re-warmed)
// showed full skeletons for the whole round-trip even though the rows were
// sitting right here in memory.
export const peekStaleHomeRow = (kind, key) =>
    (homeRowCache[kind].key === key && homeRowCache[kind].ts ? homeRowCache[kind].items : null);

// True once any row has ever loaded this session (fresh or stale) — the
// App.js re-warm uses it to tell "first warm" (always safe) from "re-roll
// under a mounted Mixpedia page" (skipped).
export const homeRowsPopulated = () =>
    ['sets', 'tracks', 'artistTracks'].some(kind => homeRowCache[kind].ts > 0);

export const homeRowsFresh = (key) =>
    cacheIsFresh('sets', key) && cacheIsFresh('tracks', key) && cacheIsFresh('artistTracks', key);

// Public ids for the home-row names: Mixpedia links entities by id, and a
// name alone can resolve to a namesake (Karotte → Kaiserdisco, audit
// 2026-10-20). resolve-artists is asked once per distinct name list and the
// answer kept for the session; only the ids of names that actually resolved
// are returned (the pairing is kept — no id is invented for an unresolved
// name). Every row request then sends BOTH `artists` (names, for an older
// server) and `artistIds` (preferred by the server when present). A failed
// resolve yields [] and the request goes out name-only, as before.
const catalogIdOrNull = (v) => (typeof v === 'string' && v.trim() && !/^\d+$/.test(v.trim())) ? v.trim() : null;
const resolvedIdsOf = (entry) => {
    // v2 server: every resolved name carries publicIds[]; older shapes below
    const pubs = Array.isArray(entry?.publicIds) ? entry.publicIds.map(catalogIdOrNull).filter(Boolean) : [];
    if (pubs.length) return pubs;
    const direct = catalogIdOrNull(entry?.publicId ?? entry?.public_id ?? entry?.id);
    if (direct) return [direct];
    const list = Array.isArray(entry?.ids) ? entry.ids : [];
    return list.map(catalogIdOrNull).filter(Boolean);
};
const homeRowArtistIdsCache = { key: null, ids: [] };
let homeRowArtistIdsInflight = null;
export const resolveHomeRowArtistIds = async (token, names) => {
    const key = names.join('|');
    if (!names.length) return [];
    if (homeRowArtistIdsCache.key === key) return homeRowArtistIdsCache.ids;
    if (homeRowArtistIdsInflight?.key === key) return homeRowArtistIdsInflight.promise;
    const promise = (async () => {
        const r = await window.electron.djpal.feedResolveArtistsV2(token, names).catch(() => null);
        if (!r || r.error) return []; // transient — retry on the next load, send names only now
        const resolved = r?.resolved || r?.data?.resolved || [];
        const byName = new Map(resolved.map(e => [String(e?.name || '').toLowerCase(), e]));
        const ids = [];
        const seen = new Set();
        for (const n of names) {
            for (const id of resolvedIdsOf(byName.get(n.toLowerCase()))) {
                if (!seen.has(id)) { seen.add(id); ids.push(id); }
            }
        }
        homeRowArtistIdsCache.key = key;
        homeRowArtistIdsCache.ids = ids;
        return ids;
    })().finally(() => { if (homeRowArtistIdsInflight?.promise === promise) homeRowArtistIdsInflight = null; });
    homeRowArtistIdsInflight = { key, promise };
    return promise;
};

// Fetch one page for a row. Returns {results, meta} or null on failure.
export const fetchHomeRowPage = async (kind, token, names, page, limit = HOME_ROW_LIMIT, seed = null) => {
    const artistIds = await resolveHomeRowArtistIds(token, names);
    const r = kind === 'sets'
        ? await window.electron.djpal.latestMixesByArtists(token, names, page, limit, artistIds).catch(() => null)
        : kind === 'artistTracks'
            ? await window.electron.djpal.tracksByArtistsRecentlyPlayed(token, names, page, limit, artistIds).catch(() => null)
            // seed → per-visit re-roll, held constant across this cache
            // load's pages so Show-more never duplicates.
            : await window.electron.djpal.latestTracksByArtists(token, names, page, limit, seed, artistIds).catch(() => null);
    if (!r || r.error || !Array.isArray(r.results)) return null;
    return r;
};

// Load page 1 into the cache (or serve the fresh cache) and return its state.
// In-flight loads are shared per (kind, key): the startup/re-warm prefetch
// and a page mount that overlap it await the same request instead of
// racing two identical ones (and, for the re-rolled tracks row, two seeds).
const homeRowInflight = { sets: null, tracks: null, artistTracks: null };
export const loadHomeRow = (kind, token, names, key, { reroll = false } = {}) => {
    if (cacheIsFresh(kind, key)) return Promise.resolve(homeRowCache[kind]);
    if (homeRowInflight[kind]?.key === key) return homeRowInflight[kind].promise;
    const promise = (async () => {
        // Seed choice = flash avoidance. When this load revalidates rows the
        // user may already be LOOKING at (stale-while-revalidate paint from
        // the in-memory cache or the persisted snapshot), the tracks row must
        // keep the SAME shuffle seed — a fresh seed makes the server return a
        // deliberately different roll, and the landed response visibly
        // replaces content mid-read ("cached then quickly replaced").
        // Re-rolling for variety is reserved for loads that can't be seen:
        // the background re-warm passes reroll:true while Mixpedia isn't the
        // active tab (App.js), and a key change / empty cache has nothing
        // painted to disturb.
        const heldSeed = !reroll
            ? (homeRowCache[kind].key === key && homeRowCache[kind].seed != null
                ? homeRowCache[kind].seed
                : (homeRowSnapshot?.key === key && homeRowSnapshot.seed != null
                    ? homeRowSnapshot.seed
                    : null))
            : null;
        const seed = heldSeed ?? Math.floor(Math.random() * 2147483647);
        const r = await fetchHomeRowPage(kind, token, names, 1, HOME_ROW_LIMIT, seed);
        if (!r) return homeRowCache[kind].key === key ? homeRowCache[kind] : { key, items: [], meta: null, ts: 0 };
        homeRowCache[kind] = { key, items: r.results, meta: r.meta || null, ts: Date.now(), seed };
        writeHomeRowSnapshot(key);
        warmImages(rowCoverUrls(kind, r.results));
        return homeRowCache[kind];
    })().finally(() => { if (homeRowInflight[kind]?.promise === promise) homeRowInflight[kind] = null; });
    homeRowInflight[kind] = { key, promise };
    return promise;
};

// Append the next page into the cache; returns the updated cache state.
export const loadHomeRowMore = async (kind, token, names, key) => {
    const cache = homeRowCache[kind];
    const nextPage = (cache.meta?.page || 1) + 1;
    const r = await fetchHomeRowPage(kind, token, names, nextPage, HOME_ROW_LIMIT, cache.seed ?? null);
    if (!r) return cache;
    // Browse-page infinite scroll: the sentinel fires at the bottom, so the
    // appended page renders immediately — start its covers alongside the
    // data landing, or they fetch lazily one by one as the grid scrolls.
    warmImages(rowCoverUrls(kind, r.results));
    homeRowCache[kind] = {
        key,
        items: [...cache.items, ...r.results],
        meta: r.meta || null,
        ts: Date.now(),
        seed: cache.seed ?? null,
    };
    return homeRowCache[kind];
};

export const rowHasMore = (cache) =>
    !!(cache?.meta?.hasNext || (cache?.items?.length || 0) < (cache?.meta?.totalCount || 0));

// Startup + periodic warmer (mirrors the For You prefetch): fill the home-row
// caches so a Mixpedia visit renders instantly instead of skeletons.
// Safe to call repeatedly — fresh caches short-circuit, in-flight loads are
// shared. App.js re-calls it whenever the TTL lapses while the window is
// visible (and on focus), so a long-open desktop app stays warm instead of
// paying the round-trip on the first visit after 15 quiet minutes.
// opts.reroll: allow the tracks row to pick a fresh shuffle seed. App.js
// passes true only while Mixpedia is NOT the active tab — variety comes from
// unseen background warms, never from replacing rows on screen.
export const prefetchHomeRows = async (dispatch, getState, { reroll = false } = {}) => {
    const state = getState();
    // Warm the follow-suggestions row too — it was the one home row with no
    // startup prefetch, so it visibly popped in later than sets/tracks.
    loadFollowSuggestions(dispatch, getState).catch(() => {});
    const names = (state.followedArtists.artists || [])
        .map(a => a.artist_name)
        .filter(Boolean)
        .slice(0, 500);
    if (!names.length) return;
    const key = homeRowCacheKey(state.auth.user?.id, names);
    if (homeRowsFresh(key)) return;
    const token = await ensureToken(dispatch, getState);
    await Promise.all([
        loadHomeRow('sets', token, names, key),
        loadHomeRow('tracks', token, names, key, { reroll }),
        loadHomeRow('artistTracks', token, names, key),
    ]);
};

// ---- Click navigation (same dispatch patterns as the entity pages) ----

export const goArtist = (dispatch, name, artistId = null, artworkUrl = null, from = 'mixpedia') => {
    if (!name) return;
    trackEvent('artist_opened', { artist_id: artistId || undefined, artist_name: name, from });
    dispatch(setActiveSearch('artist'));
    dispatch(setArtistQuery(name));
    dispatch(searchArtist({ artistName: name, artistId: artistId || null, artworkUrl: artworkUrl || null }));
};

export const goMix = (dispatch, mix, from = 'mixpedia') => {
    const title = mix.title || mix.name;
    const dj = mix.djName || mix.artist || '';
    trackEvent('mix_opened', { mix_id: mix.id || mix.publicId || undefined, artist: dj, from });
    // Id-only navigation: every catalog mix has a public id (verified: 0 of
    // 350k mixes lack one), and link is an exact unique key. No text-search
    // fallback — clicks navigate by id; only the search box searches by text.
    const filters = mix.id ? { id: mix.id }
        : mix.publicId ? { public_id: mix.publicId }
        : mix.link ? { link: mix.link }
        : null;
    if (!filters) return;
    dispatch(setActiveSearch('mix'));
    dispatch(setMixFilterContext(null));
    dispatch(setMixQuery(dj ? `${dj} - ${title}` : title));
    // The mix page's skeleton paints this mix's own cover/title/DJ while it loads.
    dispatch(setPendingMix({ title: title || '', dj, artwork: mix.artwork || mix.artworkUrl || mix.cover || null }));
    dispatch(searchMixes({ filters }));
};

// "Mixes that played this track" — same trackIncluded filter the track
// page's Played-in-Mixes card uses. Drives the plays pill on the
// "their tracks, played by other DJs" row.
export const goMixesPlayingTrack = (dispatch, track, windowDays = null) => {
    const q = track.artist ? `${track.artist} - ${track.title}` : track.title;
    if (!q) return;
    // When the click comes from the plays pill, filter the list to the same
    // recency window that produced the count — "2 plays" must open 2 sets,
    // not the track's all-time appearances.
    const dateRange = windowDays
        ? { from: new Date(Date.now() - windowDays * 86400000).toISOString().slice(0, 10), to: null }
        : undefined;
    // Id-seeded (same contract as SearchResultsPage's goTrackMixes) — the
    // name filter only runs for id-less local tracks.
    const tid = entityId(track) || null;
    dispatch(setActiveSearch('mix'));
    dispatch(setMixQuery(q));
    dispatch(setMixFilterContext({ kind: 'trackIncluded', trackName: q, trackId: tid }));
    dispatch(searchMixes({ filters: tid ? { trackId: tid, _trackName: q } : { trackIncluded: q }, dateRange }));
};

export const goTrack = (dispatch, track, from = 'mixpedia') => {
    const q = track.artist ? `${track.artist} - ${track.title}` : (track.title || '');
    if (!q) return;
    trackEvent('track_opened', { track_id: track.id || undefined, artist: track.artist, title: track.title, from });
    dispatch(setActiveSearch('track'));
    dispatch(setTrackQuery(q));
    dispatch(searchTrack({ trackName: q, trackId: entityId(track), artworkUrl: track.artworkUrl || track.artwork || null }));
};
